import _ from 'underscore';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Radium from 'radium';
import bind from 'bind-decorator';
import {
  Accordion,
  Button,
  Checkbox,
  Dropdown,
  Header,
  Icon,
  Input,
  Label,
  List,
  Message,
  Segment,
} from 'semantic-ui-react';
import moment from 'moment';
import pluralize from 'pluralize';
import Group from '@/models/group';
import Lesson from '@/models/lesson';
import User from '@/models/user';
import Course from '@/models/course';
import RoleAssignment from '@/models/role_assignment';
import ScheduledReport from '@/models/scheduled_report';
import { GROUP_TYPE_COMPLIANCE } from '../../constants';
import ModeToggle from '../Shared/ModeToggle';
import ModelTable from '../Shared/ModelTable';

const MODELS = {
  lesson: Lesson,
  user: User,
  group: Group,
  course: Course,
};

const MODEL_FILTERS = {
  lesson: {},
  user: {},
  group: { group_type__not: GROUP_TYPE_COMPLIANCE },
  course: {},
};

const FREQUENCIES = [
  { key: 1, text: 'Daily', value: 1 },
  { key: 2, text: 'Weekly', value: 2 },
  { key: 4, text: 'Bi-Weekly', value: 4 },
  { key: 3, text: 'Monthly', value: 3 },
];
const OVERNIGHT_RUN_TIME = '00:00';
const FREQUENCIES_REQUIRING_START_DATE = [2, 4, 3]; // Weekly, Bi-Weekly, Monthly

const ACTIVITY_DATE_OPTIONS = [
  { key: 'previous_week', text: 'Previous Week', value: 'previous_week' },
  { key: 'previous_two_weeks', text: 'Previous Two Weeks', value: 'previous_two_weeks' },
  { key: 'previous_month', text: 'Previous Month', value: 'previous_month' },
];

class ScheduledReportsTab extends Component {
  static propTypes = {
    reports: PropTypes.array,
  };

  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      users: [],
      error: null,
      ...this.getEmptyFormState(),
    };
  }

  async componentDidMount() {
    try {
      const [users, roles] = await Promise.all([
        User.objects().filtered({ deleted: false }).all(),
        RoleAssignment.objects().filtered({ deleted: false }).all(),
      ]);

      const userRoles = {};
      roles.forEach((assignment) => {
        userRoles[assignment.user_id] = userRoles[assignment.user_id] || [];
        userRoles[assignment.user_id].push(assignment.role_name);
      });
      const validUsers = users
        .map((user) => ({ ...user, roles: _.uniq(userRoles[user.id] || []) }))
        .filter((user) => (
          user.roles.includes('Admin')
          || user.roles.includes('Manager')
          || user.roles.includes('Owner')
        ));
      this.setState({ users: validUsers, loading: false });
    } catch (error) {
      this.setState({ error: 'Unable to load scheduled reports.', loading: false });
    }
  }

  get selectedReport() {
    return _.findWhere(this.props.reports, { name: this.state.selectedReportName });
  }

  getEmptyFormState() {
    return {
      filters: {},
      selectedReportName: null,
      frequency: 1,
      startDate: '',
      allAdmins: false,
      allManagers: false,
      recipientUsers: [],
      externalEmails: [],
      externalEmailInput: '',
      activeIndex: -1,
      editingScheduleId: null,
      editingScheduleActive: true,
    };
  }

  @bind
  mapModelsToOption(modelName) {
    const models = this.state[modelName] || [];
    let result = null;
    if (modelName === 'user') {
      result = models.map((elm) => ({ value: elm.id, text: `${elm.first_name} ${elm.last_name}` }));
    }
    if (modelName === 'group') {
      result = models.map((elm) => ({ value: elm.id, text: elm.name }));
    }
    if (modelName === 'lesson') {
      result = models.map((elm) => ({ value: elm.id, text: elm.name }));
    }
    if (modelName === 'course') {
      result = models.map((elm) => ({ value: elm.id, text: elm.name }));
    }
    return (result || []).sort((a, b) => a.text.localeCompare(b.text, 'en', { sensitivity: 'base' }));
  }

  @bind
  async loadReportFilters(name, filters = {}) {
    const report = _.findWhere(this.props.reports, { name });
    const initialFilters = {};
    if (report) {
      await Promise.all(report.dimensions.filter((dimension) => dimension.type === 'id').map(async (dimension) => {
        const filter = MODEL_FILTERS[dimension.name];
        const data = await MODELS[dimension.name].objects().filtered(filter).all();
        this.setState({ [dimension.name]: data });
      }));
      report.dimensions.filter((d) => d.type === 'date').forEach((dimension) => {
        initialFilters[dimension.name] = ACTIVITY_DATE_OPTIONS[0].value;
      });
    }
    return { ...initialFilters, ...filters };
  }

  @bind
  async setReport(name) {
    const filters = await this.loadReportFilters(name);
    this.setState({ selectedReportName: name, filters, error: null });
  }

  @bind
  updateFilter(name, value) {
    const { filters } = this.state;
    if (
      value === null
      || value === undefined
      || (Array.isArray(value) && value.length === 0)
      || value === ''
    ) delete filters[name];
    else filters[name] = value;
    this.setState({ filters });
  }

  @bind
  renderDimension(dimension) {
    const { filters } = this.state;
    if (dimension.type === 'id') {
      const options = this.mapModelsToOption(dimension.name);
      return (
        <div key={dimension.name} style={{ marginBottom: '12px' }}>
          <Header as="h4">{dimension.display_name}</Header>
          <Dropdown
            value={dimension.name in filters ? filters[dimension.name] : ['']}
            placeholder={`All ${pluralize.plural(dimension.display_name)}`}
            onChange={(e, { value }) => this.updateFilter(dimension.name, value)}
            options={options}
            search
            multiple
            selection
          />
        </div>
      );
    }
    if (dimension.type === 'date') {
      return (
        <div key={dimension.name} style={{ marginBottom: '12px' }}>
          <Header as="h4">{dimension.display_name}</Header>
          <Dropdown
            value={dimension.name in filters ? filters[dimension.name] : ACTIVITY_DATE_OPTIONS[0].value}
            onChange={(e, { value }) => this.updateFilter(dimension.name, value)}
            options={ACTIVITY_DATE_OPTIONS}
            selection
          />
        </div>
      );
    }
    if (dimension.type === 'toggle') {
      return (
        <div key={dimension.name} style={{ marginBottom: '12px' }}>
          <Checkbox
            label={dimension.display_name}
            checked={!!filters[dimension.name]}
            onChange={(e, { checked }) => {
              const state = { filters: this.state.filters };
              state.filters[dimension.name] = checked;
              this.setState(state);
            }}
            toggle
          />
        </div>
      );
    }
    return null;
  }

  @bind
  renderAdvanced(option) {
    if (option.type === 'toggle') {
      return (
        <List.Item key={option.name}>
          <Checkbox
            label={option.display_name}
            checked={!!this.state.filters[option.name]}
            onChange={(e, { checked }) => {
              const state = { filters: this.state.filters };
              state.filters[option.name] = checked;
              this.setState(state);
            }}
            style={{ marginRight: '16px', marginBottom: '12px' }}
            toggle
          />
        </List.Item>
      );
    }
    if (option.type === 'mode_toggle') {
      return (
        <List.Item key={option.name}>
          <div style={{ marginBottom: '12px' }}>
            <ModeToggle
              off={option.display_left}
              on={option.display_right}
              onToggle={(e, { checked }) => {
                const state = { filters: this.state.filters };
                state.filters[option.name] = checked;
                this.setState(state);
              }}
              control={this.state.filters[option.name]}
            />
          </div>
        </List.Item>
      );
    }
    return null;
  }

  @bind
  toggleAdvanced(e, titleProps) {
    const { index } = titleProps;
    const { activeIndex } = this.state;
    const newIndex = activeIndex === index ? -1 : index;
    this.setState({ activeIndex: newIndex });
  }

  recipientsSummary(recipients) {
    const pieces = [];
    if (recipients?.all_admins) pieces.push('All Admins');
    if (recipients?.all_managers) pieces.push('All Managers');
    const selected = this.state.users.filter((user) => recipients?.users?.includes(user.id));
    if (selected.length) {
      pieces.push(selected.map((user) => `${user.first_name} ${user.last_name}`).join(', '));
    }
    if (recipients?.emails?.length) {
      pieces.push(recipients.emails.join(', '));
    }
    return pieces.join(' | ');
  }

  @bind
  async saveSchedule() {
    const report = this.selectedReport;
    const isEditing = !!this.state.editingScheduleId;
    if (!report) {
      this.setState({ error: 'Select a report type.' });
      return;
    }
    if (
      !this.state.allAdmins
      && !this.state.allManagers
      && !this.state.recipientUsers.length
      && !this.state.externalEmails.length
    ) {
      this.setState({ error: 'Select at least one recipient.' });
      return;
    }

    const needsStartDate = FREQUENCIES_REQUIRING_START_DATE.includes(this.state.frequency);
    if (needsStartDate && !this.state.startDate) {
      this.setState({ error: 'Select a start date for the first report run.' });
      return;
    }

    const payload = {
      type: report.name,
      filters: this.state.filters,
      frequency: this.state.frequency,
      run_time: `${OVERNIGHT_RUN_TIME}:00`,
      recipients: {
        all_admins: this.state.allAdmins,
        all_managers: this.state.allManagers,
        users: this.state.recipientUsers,
        emails: this.state.externalEmails,
      },
    };
    if (needsStartDate && this.state.startDate) {
      payload.start_date = this.state.startDate;
    }
    payload.active = isEditing ? this.state.editingScheduleActive : true;
    try {
      if (isEditing) {
        await ScheduledReport.objects().update(this.state.editingScheduleId, payload);
      } else {
        await ScheduledReport.create(null, payload);
      }
      this.setState({ ...this.getEmptyFormState(), error: null });
      if (this.modelTableUpdateMethod) {
        await this.modelTableUpdateMethod();
      }
    } catch (error) {
      this.setState({ error: 'Unable to save scheduled report.' });
    }
  }

  @bind
  async editSchedule(schedule) {
    const filters = await this.loadReportFilters(schedule.type, schedule.filters || {});
    this.setState({
      selectedReportName: schedule.type,
      filters,
      frequency: schedule.frequency,
      startDate: FREQUENCIES_REQUIRING_START_DATE.includes(schedule.frequency)
        ? moment.utc(schedule.next_run_at).format('YYYY-MM-DD')
        : '',
      allAdmins: !!schedule.recipients?.all_admins,
      allManagers: !!schedule.recipients?.all_managers,
      recipientUsers: schedule.recipients?.users || [],
      externalEmails: schedule.recipients?.emails || [],
      externalEmailInput: '',
      activeIndex: -1,
      editingScheduleId: schedule.id,
      editingScheduleActive: schedule.active,
      error: null,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  @bind
  cancelEditing() {
    this.setState({ ...this.getEmptyFormState(), error: null });
  }

  @bind
  async setScheduleActive(id, active) {
    try {
      await ScheduledReport.objects().update(id, { active });
      this.setState((prev) => ({
        error: null,
        editingScheduleActive: prev.editingScheduleId === id ? active : prev.editingScheduleActive,
      }));
      if (this.modelTableUpdateMethod) {
        await this.modelTableUpdateMethod();
      }
    } catch (error) {
      this.setState({ error: 'Unable to update scheduled report status.' });
    }
  }

  @bind
  async deleteSchedule(id) {
    try {
      await ScheduledReport.objects().update(id, { deleted: true, active: false });
      this.setState((prev) => (
        prev.editingScheduleId === id
          ? { ...this.getEmptyFormState(), error: null }
          : { error: null }
      ));
      if (this.modelTableUpdateMethod) {
        await this.modelTableUpdateMethod();
      }
    } catch (error) {
      this.setState({ error: 'Unable to delete scheduled report.' });
    }
  }

  @bind
  getScheduledReportsUpdateHandler(modelTableUpdateMethod) {
    this.modelTableUpdateMethod = modelTableUpdateMethod;
  }

  render() {
    if (this.state.loading) return <p>Loading scheduled reports...</p>;
    const isEditing = !!this.state.editingScheduleId;
    const report = this.selectedReport;
    const reportOptions = this.props.reports
      .filter((r) => !r.disabled)
      .map((r) => ({ key: r.name, value: r.name, text: r.display_name }));
    const recipientOptions = _.sortBy(this.state.users, ['first_name', 'last_name']).map((u) => ({
      key: u.id,
      value: u.id,
      text: `${u.first_name} ${u.last_name} (${u.roles.join('/')})`,
    }));
    const reportTypeFilters = this.props.reports.reduce((acc, item) => {
      acc[item.name] = item.display_name;
      return acc;
    }, {});
    const frequencyFilters = FREQUENCIES.reduce((acc, item) => {
      acc[item.value] = item.text;
      return acc;
    }, {});
    const statusFilters = {
      true: 'Active',
      false: 'Paused',
    };
    const scheduledReportColumns = [
      {
        name: 'Created',
        sort: true,
        key: ['created_at', (createdAt) => moment(createdAt).format('YYYY-MM-DD HH:mm')],
      },
      {
        name: 'Report Type',
        sort: true,
        filter: reportTypeFilters,
        key: ['type', (type) => reportTypeFilters[type] || type],
      },
      {
        name: 'Frequency',
        sort: true,
        filter: frequencyFilters,
        key: ['frequency', (frequency) => frequencyFilters[frequency] || frequency],
      },
      {
        name: 'Recipients',
        key: ['recipients', (recipients) => this.recipientsSummary(recipients)],
      },
      {
        name: 'Next Run',
        sort: true,
        key: ['next_run_at', (nextRunAt) => moment(nextRunAt).format('YYYY-MM-DD')],
      },
      {
        name: 'Last Run Status',
        key: ['this', (sched) => {
          const errors = sched.last_run_errors;
          if (!errors || errors.length === 0) {
            return <Icon name="check circle" color="green" />;
          }
          return (
            <div>
              {errors.map((err) => {
                const user = this.state.users.find((u) => u.id === err.user_id);
                let label = 'External';
                if (user) {
                  label = `${user.first_name} ${user.last_name}`;
                } else if (err.user_id) {
                  label = 'Deleted User';
                }
                return (
                  <div key={`${err.user_id}-${err.error_text}`} style={{ color: 'red' }}>
                    <Icon name="exclamation circle" />{label}
                  </div>
                );
              })}
            </div>
          );
        }],
      },
      {
        name: 'Status',
        sort: true,
        filter: statusFilters,
        key: ['active', (active) => (active ? 'Active' : 'Paused')],
      },
      {
        name: 'Actions',
        key: ['this', (sched) => (
          <Dropdown
            icon="ellipsis vertical"
            floating
            className="icon"
            button
            aria-label="Scheduled report actions"
            title="Scheduled report actions"
          >
            <Dropdown.Menu>
              <Dropdown.Item text="Edit" onClick={() => this.editSchedule(sched)} />
              <Dropdown.Item
                text={sched.active ? 'Pause' : 'Resume'}
                onClick={() => this.setScheduleActive(sched.id, !sched.active)}
              />
              <Dropdown.Item
                text="Delete"
                onClick={() => this.deleteSchedule(sched.id)}
                style={{ color: 'red' }}
              />
            </Dropdown.Menu>
          </Dropdown>
        )],
      },
    ];
    return (
      <div style={{ margin: '40px 15%' }}>
        <Segment style={{ position: 'relative', zIndex: 200 }}>
          <Header as="h3">{isEditing ? 'Edit Scheduled Report' : 'Create Scheduled Report'}</Header>
          {this.state.error && <Message negative content={this.state.error} />}
          <Header as="h4">Report Type</Header>
          <Dropdown
            placeholder="Select a report"
            selection
            options={reportOptions}
            value={this.state.selectedReportName}
            onChange={(e, { value }) => this.setReport(value)}
          />
          {report && (
            <div style={{ marginTop: '16px' }}>
              <Header as="h4">Parameters</Header>
              {report.dimensions.map(this.renderDimension)}
              {report.advanced && (
                <Accordion>
                  <Accordion.Title
                    active={this.state.activeIndex === 0}
                    index={0}
                    onClick={this.toggleAdvanced}
                  >
                    <Icon name="dropdown" />
                    Advanced Options
                  </Accordion.Title>
                  <Accordion.Content active={this.state.activeIndex === 0}>
                    <List>
                      {report.advanced.map(this.renderAdvanced)}
                    </List>
                  </Accordion.Content>
                </Accordion>
              )}
            </div>
          )}
          <div style={{ marginTop: '16px' }}>
            <Header as="h4">Frequency</Header>
            <Dropdown
              selection
              options={FREQUENCIES}
              value={this.state.frequency}
              onChange={(e, { value }) => this.setState((prev) => ({
                frequency: value,
                startDate: FREQUENCIES_REQUIRING_START_DATE.includes(value) ? prev.startDate : '',
              }))}
              style={{ marginRight: '10px' }}
            />
            <span>Runs overnight automatically</span>
          </div>
          {FREQUENCIES_REQUIRING_START_DATE.includes(this.state.frequency) && (
            <div style={{ marginTop: '16px' }}>
              <Header as="h4">Start Date</Header>
              <Input
                type="date"
                value={this.state.startDate}
                onChange={(e, { value }) => this.setState({ startDate: value })}
              />
              <span style={{ marginLeft: '10px' }}>
                Date of the next report run; subsequent runs are offset from this date
              </span>
            </div>
          )}
          <div style={{ marginTop: '16px' }}>
            <Header as="h4">Recipients</Header>
            <Checkbox
              label="All Admins"
              checked={this.state.allAdmins}
              onChange={(e, { checked }) => this.setState({ allAdmins: checked })}
              style={{ marginRight: '16px' }}
            />
            <Checkbox
              label="All Managers"
              checked={this.state.allManagers}
              onChange={(e, { checked }) => this.setState({ allManagers: checked })}
            />
            <div style={{ marginTop: '12px' }}>
              <Dropdown
                placeholder="Specific manager/admin recipients"
                fluid
                multiple
                search
                selection
                options={recipientOptions}
                value={this.state.recipientUsers}
                onChange={(e, { value }) => this.setState({ recipientUsers: value })}
              />
            </div>
            <div>
              <div style={{ marginTop: '12px' }}>
                <Input
                  placeholder="External email address"
                  value={this.state.externalEmailInput}
                  onChange={(e, { value }) => this.setState({ externalEmailInput: value })}
                  action={(
                    <Button
                      type="button"
                      onClick={() => {
                        const email = this.state.externalEmailInput.trim();
                        if (email && !this.state.externalEmails.includes(email)) {
                          this.setState((prev) => ({
                            externalEmails: [...prev.externalEmails, email],
                            externalEmailInput: '',
                          }));
                        }
                      }}
                    >
                      Add
                    </Button>
                  )}
                />
              </div>
              {this.state.externalEmails.length > 0 && (
                <div style={{ marginTop: '8px' }}>
                  {this.state.externalEmails.map((email) => (
                    <Label key={email} style={{ marginRight: '4px', marginBottom: '4px' }}>
                      {email}
                      <Icon
                        name="delete"
                        onClick={() => this.setState((prev) => ({
                          externalEmails: prev.externalEmails.filter((e) => e !== email),
                        }))}
                      />
                    </Label>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div style={{ marginTop: '16px' }}>
            <Button positive onClick={this.saveSchedule}>
              {isEditing ? 'Update Scheduled Report' : 'Save Scheduled Report'}
            </Button>
            {isEditing && (
              <Button style={{ marginLeft: '8px' }} onClick={this.cancelEditing}>
                Cancel
              </Button>
            )}
          </div>
        </Segment>

        <ModelTable
          title="Organization Scheduled Reports"
          model={ScheduledReport}
          baseFilter={{ deleted: false }}
          columns={scheduledReportColumns}
          defaultSort={['created_at', 'dsc']}
          updateHandler={this.getScheduledReportsUpdateHandler}
        />
      </div>
    );
  }
}

export default Radium(ScheduledReportsTab);
